import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { sendScheduleLink } from '../../utils/api';

export default function SendScheduleLinksModal({ open, onClose, showToast, teamName, people = [], senderEmail, canSend, templateId }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Per-person selection — leads choose who gets a link (default: everyone).
  const [checked, setChecked] = useState(() => new Set());

  // People with valid emails who are rostered
  const eligible = people.filter(p => p.email && p.id);

  useEffect(() => {
    if (open) setChecked(new Set(people.filter(p => p.email && p.id).map(p => p.id)));
  }, [open, people]);

  const toggleChecked = (id) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selected = eligible.filter(p => checked.has(p.id));
  const allChecked = eligible.length > 0 && selected.length === eligible.length;

  async function handleSendAll() {
    if (!canSend) {
      showToast('Only @thebelonging.co staff can send schedule links.');
      return;
    }
    if (selected.length === 0) {
      showToast('Select at least one person to send to.');
      return;
    }
    setSending(true);
    let successCount = 0;
    let errorCount = 0;
    for (const person of selected) {
      try {
        await sendScheduleLink(person.email, person.name, person.id, templateId || 'schedule-link', senderEmail);
        successCount++;
      } catch (err) {
        console.error(`Failed to send to ${person.name}:`, err);
        errorCount++;
      }
    }
    setSending(false);
    setSent(true);
    if (errorCount === 0) {
      showToast(`Schedule links sent to ${successCount} people.`);
    } else {
      showToast(`Sent ${successCount}, failed ${errorCount}. Check Apps Script logs.`);
    }
    onClose();
    setSent(false);
  }

  function handleClose() {
    if (sending) return;
    setSent(false);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Send Schedule Links"
      footer={
        <>
          <button className="btn" onClick={handleClose} disabled={sending}>Cancel</button>
          <button
            className={`btn btn-dark${(!canSend || selected.length === 0 || sending) ? ' btn-disabled' : ''}`}
            onClick={handleSendAll}
            disabled={!canSend || selected.length === 0 || sending}
          >
            {sending ? 'Sending…' : `Send to ${selected.length} ${selected.length === 1 ? 'Person' : 'People'}`}
          </button>
        </>
      }
    >
      {!canSend && (
        <div className="notice-bar" style={{ background: '#fff8ed', border: '1px solid #f5c542', color: '#8a6200' }}>
          Only @thebelonging.co staff can send schedule links.
        </div>
      )}
      {canSend && (
        <div className="notice-bar">
          This will send personalized schedule links to all rostered members of {teamName || 'this team'} who have a valid email address.
          Safe to use as a re-send any time — e.g. the week of the conference, so everyone has their link fresh in their inbox.
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>
          Will be sent to {selected.length} of {eligible.length} {eligible.length === 1 ? 'person' : 'people'}
          {eligible.length > 0 && (
            <button
              className="btn btn-xs"
              style={{ marginLeft: 10, textTransform: 'none', letterSpacing: 'normal' }}
              onClick={() => setChecked(allChecked ? new Set() : new Set(eligible.map(p => p.id)))}
            >
              {allChecked ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </div>
        {eligible.length === 0 ? (
          <div style={{ padding: '16px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            No rostered members with email addresses found.
          </div>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
            {eligible.map(p => (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: checked.has(p.id) ? 'var(--white)' : 'var(--off)' }}>
                <input
                  type="checkbox"
                  checked={checked.has(p.id)}
                  onChange={() => toggleChecked(p.id)}
                  style={{ accentColor: 'var(--black)', flexShrink: 0 }}
                />
                <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)', opacity: checked.has(p.id) ? 1 : 0.55 }}>
                  {p.name}
                  <span style={{ fontFamily: 'var(--font-b)', fontWeight: 300, color: 'var(--muted)', marginLeft: 8 }}>{p.email}</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
